import {
  Banner,
  BlockStack,
  Button,
  Checkbox,
  Divider,
  DropZone,
  InlineStack,
  Modal,
  Popover,
  Spinner,
  Text,
  Tooltip,
} from "@shopify/polaris"
import { DeleteIcon } from "@shopify/polaris-icons"
import type { AxiosResponse, Method } from "axios"
import axios from "axios"
import classnames from "classnames"
import { observer } from "mobx-react"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import commonApi from "@/api/common"
import {
  getThemeLayoutMediaImagePreviewUrl,
  ThemeLayoutFilesListItem,
} from "@/api/shopify-graphql/shopify-files"
import { SpinnerContainer } from "@/component"
import { ThemeImageBannerRef } from "@/models/ITrackingPageOptions"
import stores from "@/stores"
import toast from "@/utils/toast"

import styles from "./ImageUpload.module.scss"
import { isThemeImageMimeOrExtensionAllowed, validateThemeImageFile } from "./themeImageValidation"

const ACCEPT_MIME = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
  "image/gif",
].join(",")

const COMMON_IMAGE_POLL_MS = 1500
const COMMON_IMAGE_UPLOAD_WAIT_MS = 90_000

/**
 * 注册后若 url 为空，通过 show 轮询直到有 CDN 地址或超时
 */
const waitForUploadedImageReady = async (gid: string): Promise<void> => {
  const deadline = Date.now() + COMMON_IMAGE_UPLOAD_WAIT_MS

  while (Date.now() < deadline) {
    const { data } = await commonApi.getCommonImageShow({ gid })
    const url = data.image?.url?.trim() ?? ""

    if (url) {
      return
    }
    await new Promise((r) => setTimeout(r, COMMON_IMAGE_POLL_MS))
  }

  throw new Error("Image processing timed out")
}

/**
 * 将 staged-upload parameters 转换为 HTTP 请求头
 * content_type → Content-Type，其余 snake_case → Header-Case
 */
const stagedParamsToHeaders = (
  parameters: { name: string; value: string }[],
): Record<string, string> => {
  const result: Record<string, string> = {}

  for (const { name, value } of parameters) {
    if (name === "content_type") {
      result["Content-Type"] = value
    } else {
      result[name.replace(/_/g, "-")] = value
    }
  }

  return result
}

/**
 * 三步上传流程：
 * 1. 获取 staged-upload 临时链接
 * 2. 直接 PUT 文件到 GCS（绕过 baseRequest 拦截器，不带 Shopify auth）
 * 3. 注册 Shopify MediaImage，返回 gid
 */
const stagedUploadFile = async (file: File): Promise<string> => {
  const { data: stagedData } = await commonApi.getStagedUpload({
    filename: file.name,
    mime_type: file.type,
    size_bytes: file.size,
  })

  const { staged_target } = stagedData
  const headers = stagedParamsToHeaders(staged_target.parameters)

  await axios.request({
    method: staged_target.method as Method,
    url: staged_target.url,
    data: file,
    headers,
    // 不经过 baseRequest 拦截器，避免注入 Shopify Authorization
    baseURL: "",
    timeout: COMMON_IMAGE_UPLOAD_WAIT_MS,
  })

  const { data: registerData } = await commonApi.registerCommonImage({
    filename: file.name,
    mime_type: file.type,
    resource_url: staged_target.resource_url,
    signature: staged_target.signature,
    size_bytes: file.size,
    time: staged_target.time,
  })

  return registerData.image.gid
}

const pickServerMessage = (data: unknown): string | undefined => {
  if (!data || typeof data !== "object") {
    return undefined
  }
  const d = data as Record<string, unknown>
  const candidates = ["msg", "message", "error", "detail"]

  for (const key of candidates) {
    const v = d[key]

    if (typeof v === "string" && v.trim()) {
      return v.trim()
    }
  }

  return undefined
}

const getCommonImageUploadErrorMessage = (
  err: unknown,
  t: (key: string) => string,
): string => {
  const res = err as Partial<AxiosResponse<{ msg?: string; message?: string }>>

  if (res?.status === 413) {
    return t("tracking_page:ThemeImageUpload413")
  }

  if (res?.status === 415) {
    return t("tracking_page:ThemeImageUpload415")
  }

  if (res?.status === 422) {
    return t("tracking_page:ThemeImageUpload422")
  }

  const fromBody = pickServerMessage(res?.data)

  if (fromBody) {
    return fromBody
  }

  return t("tracking_page:ThemeImageUploadFailed")
}

const pickThemeImageFileFromDrop = (
  dropFiles: File[],
  acceptedFiles: File[],
  rejectedFiles: File[],
): File | undefined => {
  if (acceptedFiles[0]) {
    return acceptedFiles[0]
  }

  if (dropFiles[0]) {
    return dropFiles[0]
  }

  const rejected = rejectedFiles[0]

  if (rejected && isThemeImageMimeOrExtensionAllowed(rejected)) {
    return rejected
  }

  return undefined
}

/** Figma Frame 32–37：单卡总高 156px，上图区域 124px */
const GALLERY_CARD_H = 156
const GALLERY_IMAGE_H = 124

const themeImageValidationToast = (
  t: (key: string) => string,
  messageKey: string,
): string => {
  switch (messageKey) {
    case "ThemeImageValidationSize":
      return t("tracking_page:ThemeImageValidationSize")
    case "ThemeImageValidationMegapixels":
      return t("tracking_page:ThemeImageValidationMegapixels")
    case "ThemeImageValidationAspect":
      return t("tracking_page:ThemeImageValidationAspect")
    case "ThemeImageValidationType":
      return t("tracking_page:ThemeImageValidationType")
    case "ThemeImageValidationFilename":
      return t("tracking_page:ThemeImageValidationFilename")
    default:
      return t("tracking_page:ThemeImageUploadFailed")
  }
}


/** 图库文件名超过此长度时显示 Tooltip 展示完整文本 */
const GALLERY_LABEL_TOOLTIP_THRESHOLD = 20

interface ThemeImageGalleryItemProps {
  item: ThemeLayoutFilesListItem
  checked: boolean
  selectedId: string | null
  galleryItemDomId: string
  onSelect: (id: string) => void
  onClearSelection: () => void
  onDelete: (id: string) => void
  deleteLabel: string
  deleting: boolean
  deleteDisabled: boolean
  /** 删除确认 Popover 是否打开（由父组件统一管理，保证同一时刻只有一个打开） */
  deletePopoverActive: boolean
  onDeletePopoverChange: (active: boolean) => void
}

const ThemeImageGalleryItem = (props: ThemeImageGalleryItemProps) => {
  const { t } = useTranslation(["tracking_page", "common"])
  const {
    item,
    checked,
    selectedId,
    galleryItemDomId,
    onSelect,
    onClearSelection,
    onDelete,
    deleteLabel,
    deleting,
    deleteDisabled,
    deletePopoverActive,
    onDeletePopoverChange,
  } = props

  const handleDeletePopoverClose = useCallback(() => {
    onDeletePopoverChange(false)
  }, [onDeletePopoverChange])

  const handleDeleteConfirm = useCallback(() => {
    onDeletePopoverChange(false)
    onDelete(item.id)
  }, [item.id, onDelete, onDeletePopoverChange])

  const handleToggleSelection = () => {
    if (checked) {
      onClearSelection()
      return
    }
    onSelect(item.id)
  }

  const shouldShowLabelTooltip = item.label.length > GALLERY_LABEL_TOOLTIP_THRESHOLD
  const labelNode = (
    <span className="min-w-0 flex-1 truncate text-left">
      {item.label.slice(0, GALLERY_LABEL_TOOLTIP_THRESHOLD + 8)}
    </span>
  )

  return (
    <div
      className="group flex w-full max-w-[145px] flex-col"
      style={{ minHeight: GALLERY_CARD_H }}
    >
      <div
        className={`relative w-full shrink-0 overflow-hidden rounded-lg border border-solid border-[#ebebeb] ${
          checked
            ? "bg-[#cccccc]"
            : "hover:bg-[#cccccc]"
        }`}
        style={{ height: GALLERY_IMAGE_H }}
        role="button"
        tabIndex={0}
        aria-pressed={checked}
        aria-label={item.label}
        onClick={handleToggleSelection}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault()
            handleToggleSelection()
          }
        }}
      >
        <img
          src={item.thumbnailUrl}
          alt=""
          className="absolute inset-0 h-full w-full object-contain object-center z-10"
          loading="lazy"
          decoding="async"
        />
        {checked ? (
          <div
            className="pointer-events-none absolute inset-0 z-2"
            aria-hidden
          />
        ) : null}
        <div
          className="absolute left-2 top-2 z-10"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <Checkbox
            id={galleryItemDomId}
            label={item.label}
            labelHidden
            checked={checked}
            onChange={(newChecked) => {
              if (newChecked) {
                onSelect(item.id)
                return
              }
              if (selectedId === item.id) {
                onClearSelection()
              }
            }}
          />
        </div>
      </div>

      <div
        className="mt-auto flex min-h-[28px] w-full shrink-0 items-center justify-between gap-1 pt-1"
      >
        <p className={classnames("truncate", styles.truncate)}>{shouldShowLabelTooltip ? (
          <Tooltip preferredPosition={"mostSpace"} content={item.label}>
            {labelNode}
          </Tooltip>
        ) : labelNode}
        </p>
        {item.deletable !== false ? (
          <div
            className={classnames(
              "shrink-0 transition-opacity duration-150",
              styles.deleteActionWrapper,
              deletePopoverActive || deleting ? "flex" : "hidden group-hover:flex",
            )}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          >
            <Popover
              active={deletePopoverActive}
              activator={
                <Button
                  variant="tertiary"
                  icon={DeleteIcon}
                  accessibilityLabel={deleteLabel}
                  onClick={() => onDeletePopoverChange(true)}
                  loading={deleting}
                  disabled={deleteDisabled}
                />
              }
              preferredAlignment="left"
              sectioned
              onClose={handleDeletePopoverClose}
            >
              <div className={""}>
                <BlockStack gap="300">
                  <Text as="p" variant="bodyMd">
                    {t("tracking_page:ThemeImageDeleteConfirm")}
                  </Text>

                  <InlineStack gap="200" align="end">
                    <Button onClick={handleDeletePopoverClose} size={"micro"}>
                      {t("common:Cancel")}
                    </Button>
                    <Button
                      tone="critical"
                      variant="primary"
                      onClick={handleDeleteConfirm}
                      loading={deleting}
                      size={"micro"}
                    >
                      {t("common:Delete")}
                    </Button>
                  </InlineStack>
                </BlockStack>
              </div>
            </Popover>
          </div>
        ) : null}
      </div>
    </div>
  )
}

const ThemeImageSelectModal = observer((props: {
  open: boolean
  onClose: () => void
  onConfirm: (file: ThemeImageBannerRef) => void | Promise<void>
  /** 打开弹窗时预选中的图库项 id（与 banner.shopifyFileId 一致） */
  currentImageId?: string
}) => {
  const { open, onClose, onConfirm, currentImageId } = props
  const { t } = useTranslation(["tracking_page", "common"])
  const gallery = stores.commonStore.themeLayoutCommonImages
  const galleryLoading = stores.commonStore.themeLayoutCommonImagesLoading
  const galleryLoadingMore = stores.commonStore.themeLayoutCommonImagesLoadingMore
  const galleryHasMore = stores.commonStore.themeLayoutCommonImagesHasMore
  const [uploading, setUploading] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [errorText, setErrorText] = useState<string>("")
  const [dropZoneError, setDropZoneError] = useState<string>("")
  const [deletingId, setDeletingId] = useState<string | null>(null)
  // 当前打开删除确认 Popover 的图库项 id；统一在父级管理以保证排他（同时只开一个）
  const [deletePopoverId, setDeletePopoverId] = useState<string | null>(null)
  // 首次打开/重新加载时的“准备中”遮罩：接口返回后列表仍可能再次变更顺序，
  // 这里把 loading 维持到顺序稳定（最后一次加载结束 + 两帧）后再撤掉，避免“先显示后跳序”。
  const [preparing, setPreparing] = useState(false)
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null)

  const loadGallery = useCallback(async () => {
    setErrorText("")
    try {
      // 把当前选中的图片 gid 一起传给接口，确保其在列表中的位置稳定
      await stores.commonStore.fetchThemeLayoutCommonImages({
        gid: currentImageId ?? "",
      })
    } catch (e) {
      setErrorText(e instanceof Error ? e.message : String(e))
    }
  }, [currentImageId])

  useEffect(() => {
    if (!open) {
      return
    }
    setPreparing(true)
    void loadGallery()
  }, [open, loadGallery])

  // 接口加载结束后，等列表顺序稳定（再过两帧、且未再次进入 loading）才撤掉准备遮罩。
  // 依赖 galleryLoading：若期间又触发一次加载（顺序可能再变），会取消本次计时并重排，
  // 直到最后一次加载结束后顺序不再变化，再结束 loading。
  useEffect(() => {
    if (!open || galleryLoading) {
      return
    }

    let raf2 = 0
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setPreparing(false))
    })

    return () => {
      cancelAnimationFrame(raf1)
      cancelAnimationFrame(raf2)
    }
  }, [open, galleryLoading])

  // 滚动接近底部时（哨兵进入滚动容器视野）追加下一页。不再自设限高，复用 Modal 自身的滚动区
  // （Polaris .Polaris-Scrollable）作为 root，避免用视口做 root 时被 modal overflow 裁剪导致误判而一次性加载所有页。
  useEffect(() => {
    if (!open || !galleryHasMore) {
      return
    }
    const target = loadMoreSentinelRef.current
    const root = target?.closest(".Polaris-Scrollable") as HTMLElement | null

    if (!root || !target) {
      return
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void stores.commonStore.loadMoreThemeLayoutCommonImages()
        }
      },
      { root, rootMargin: "200px" },
    )

    io.observe(target)

    return () => io.disconnect()
  }, [open, galleryHasMore, gallery.length])

  useEffect(() => {
    if (!open) {
      setSelectedId(null)
      setDropZoneError("")
      setDeletePopoverId(null)
      setPreparing(false)
      return
    }

    if (!currentImageId) {
      setSelectedId(null)
      return
    }

    const existsInGallery = gallery.some((item) => item.id === currentImageId)

    setSelectedId(existsInGallery ? currentImageId : null)
  }, [open, currentImageId, gallery])

  const handleDrop = useCallback(
    async (dropFiles: File[], acceptedFiles: File[], rejectedFiles: File[]) => {
      const file = pickThemeImageFileFromDrop(dropFiles, acceptedFiles, rejectedFiles)

      if (!file) {
        setDropZoneError(t("tracking_page:ThemeImageValidationType"))
        return
      }

      const check = await validateThemeImageFile(file)

      if (!check.ok && check.messageKey) {
        setDropZoneError(themeImageValidationToast(t, check.messageKey))
        return
      }
      setUploading(true)
      setErrorText("")
      setDropZoneError("")
      let shouldRefreshGallery = false

      try {
        const gid = await stagedUploadFile(file)

        const { data: showData } = await commonApi.getCommonImageShow({ gid })

        if (!showData.image?.url?.trim()) {
          await waitForUploadedImageReady(gid)
        }

        setDropZoneError("")
        shouldRefreshGallery = true
      } catch (e) {
        const message = e instanceof Error && e.message === "Image processing timed out"
          ? t("tracking_page:ThemeImageUploadProcessingTimeout")
          : getCommonImageUploadErrorMessage(e, t)

        setDropZoneError(message)
      } finally {
        setUploading(false)
      }

      if (shouldRefreshGallery) {
        void loadGallery()
      }
    },
    [loadGallery, t],
  )

  const handleDeleteGalleryFile = useCallback(
    async (id: string) => {
      if (deletingId) {
        return
      }
      const target = stores.commonStore.themeLayoutCommonImages.find((g) => g.id === id)

      if (target && target.deletable === false) {
        return
      }
      setDeletingId(id)
      try {
        await commonApi.deleteCommonImage(id)

        // 删除成功后通知定制页：遍历当前配置，悄悄移除所有引用该 gid 的地方（含当前正在使用的那张）
        // 并同步元数据，确保不触发全局保存提醒。id 即图片 gid（= banner.shopifyFileId）。
        stores.themeLayoutConfigStore.emitCommonImageDeleted(id)
        // 等待 gallery 刷新完成后再释放锁，确保 item 从列表消失前无法重复点击删除
        await loadGallery()
      } catch (e) {
        toast(
          `${t("tracking_page:ThemeImageUploadFailed")}: ${e instanceof Error ? e.message : String(e)}`,
          4e3,
          true,
        )
      } finally {
        setDeletingId(null)
      }
    },
    [deletingId, loadGallery, t],
  )

  const handleDone = useCallback(async () => {
    if (!selectedId) {
      return
    }
    try {
      const selected = stores.commonStore.themeLayoutCommonImages.find((g) => g.id === selectedId)
      let url = selected?.thumbnailUrl ?? null

      if (!url) {
        url = await getThemeLayoutMediaImagePreviewUrl(selectedId)
      }

      if (!url) {
        toast(t("tracking_page:ThemeImageUploadFailed"), 3e3, true)
        return
      }
      await Promise.resolve(onConfirm({ shopifyFileId: selectedId, url }))
      onClose()
    } catch (e) {
      toast(
        `${t("tracking_page:ThemeImageUploadFailed")}: ${e instanceof Error ? e.message : String(e)}`,
        4e3,
        true,
      )
    }
  }, [onClose, onConfirm, selectedId, t])

  const deleteAria = String(t("common:Remove"))
  const isSelectionUnchanged = Boolean(
    currentImageId && selectedId && selectedId === currentImageId,
  )

  return (
    <Modal
      size="large"
      open={open}
      onClose={onClose}
      title={t("tracking_page:ThemeImageSelectTitle")}
      primaryAction={{
        content: t("common:Done"),
        onAction: () => void handleDone(),
        disabled: uploading || !selectedId || isSelectionUnchanged,
      }}
      secondaryActions={[
        {
          content: t("common:Cancel"),
          onAction: onClose,
          disabled: uploading,
        },
      ]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          {/* Figma Drop zone：虚线 #8A8A8A、底 #FDFDFD、内边距 32、按钮 + 说明间距 8 */}
          {dropZoneError ? (
            <Banner
              tone="critical"
              onDismiss={() => setDropZoneError("")}
            >
              <p>{dropZoneError}</p>
            </Banner>
          ) : null}
          <SpinnerContainer loading={uploading}>
            <DropZone
              accept={ACCEPT_MIME}
              allowMultiple={false}
              onDrop={handleDrop}
              variableHeight
            >
              <DropZone.FileUpload
                actionTitle={t("tracking_page:ThemeImageAddImage")}
                actionHint={t("tracking_page:ThemeImageAcceptHint")}
              />
            </DropZone>
          </SpinnerContainer>

          {errorText ? (
            <Banner tone="critical">
              <p>{errorText}</p>
            </Banner>
          ) : null}

          <div className="relative min-h-[100px]">
            <div
              className="grid w-full gap-4"
              style={{
                gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
              }}
            >
              {!galleryLoading && !preparing && gallery.length === 0 ? (
                <div className="col-span-full">
                  <Text as="p" variant="bodyMd" tone="subdued">
                    {t("tracking_page:ThemeImageGalleryEmpty")}
                  </Text>
                </div>
              ) : (
                gallery.map((item) => {
                  const checked = selectedId === item.id
                  const galleryItemDomId = `theme-image-gallery-${item.id.replace(/[^a-zA-Z0-9_-]/g, "_")}`

                  return (
                    <ThemeImageGalleryItem
                      key={item.id}
                      item={item}
                      checked={checked}
                      selectedId={selectedId}
                      galleryItemDomId={galleryItemDomId}
                      onSelect={(id) => setSelectedId(id)}
                      onClearSelection={() => setSelectedId(null)}
                      onDelete={(id) => void handleDeleteGalleryFile(id)}
                      deleteLabel={deleteAria}
                      deleting={deletingId === item.id}
                      deleteDisabled={deletingId !== null && deletingId !== item.id}
                      deletePopoverActive={deletePopoverId === item.id}
                      onDeletePopoverChange={(active) =>
                        setDeletePopoverId(active ? item.id : null)
                      }
                    />
                  )
                })
              )}
            </div>

            {/* 触底哨兵：进入视野即加载下一页；加载中显示 spinner */}
            {galleryHasMore ? (
              <div ref={loadMoreSentinelRef} className="flex justify-center py-4">
                {galleryLoadingMore ? (
                  <Spinner size="small" accessibilityLabel={t("tracking_page:ThemeImageLoading")} />
                ) : null}
              </div>
            ) : null}

            {/* 准备遮罩：列表在底层挂载并完成排序，遮罩盖住期间的“跳序”，稳定后再撤掉；spinner 垂直居中 */}
            {galleryLoading || preparing ? (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white">
                <Spinner accessibilityLabel={t("tracking_page:ThemeImageLoading")} />
              </div>
            ) : null}
          </div>
        </BlockStack>
      </Modal.Section>
    </Modal>
  )
})

export interface ImageUploadProps {
  banner: ThemeImageBannerRef | undefined
  onPersistBanner: (next: ThemeImageBannerRef | undefined) => Promise<void>
}

const ImageUpload = (props: ImageUploadProps) => {
  const { banner, onPersistBanner } = props
  const { t } = useTranslation(["tracking_page", "common"])
  const [modalOpen, setModalOpen] = useState(false)
  const [busy, setBusy] = useState(false)


  const handleOpenModal = useCallback(() => {
    setModalOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setModalOpen(false)
  }, [])

  const handleConfirmSelection = useCallback(
    async (file: ThemeImageBannerRef) => {
      try {
        await onPersistBanner(file)
      } catch (e) {
        console.error(e)
        toast(t("tracking_page:ThemeImageUploadFailed"), 3e3, true)
      }
    },
    [onPersistBanner, t],
  )

  const handleRemove = useCallback(async () => {
    setBusy(true)
    try {
      await onPersistBanner(undefined)
    } catch (e) {
      console.error(e)
      toast(t("tracking_page:ThemeImageUploadFailed"), 3e3, true)
    } finally {
      setBusy(false)
    }
  }, [onPersistBanner, t])

  const emptyState = (
    <div className="flex min-h-[120px] flex-col items-center justify-center rounded-lg border border-dashed border-[#8a8a8a] bg-[#fdfdfd] px-4 py-6">
      <Button onClick={handleOpenModal}>{t("tracking_page:ThemeImageAddImage")}</Button>
      <div className="mt-2 text-center">
        <Text as="p" variant="bodySm" tone="subdued">
          {t("tracking_page:ThemeImageAcceptHint")}
        </Text>
      </div>
    </div>
  )

  const previewState = banner?.url ? (
    <div className="overflow-hidden rounded-lg bg-[#f6f6f7]" style={{ border: "1px solid rgba(0, 0, 0, 0.13)" }}>
      <div className="flex max-h-[220px] min-h-[160px] items-center justify-center bg-[#ececec]">
        <img src={banner.url} alt="" className="max-h-[204px] max-w-full object-contain" />
      </div>
      <div className={classnames("grid grid-cols-2 divide-x divide-gray-200 border-t border-[#B5B5B5]", styles.buttonFullWrapper)}>
        <Button fullWidth variant="tertiary" onClick={handleOpenModal} disabled={busy}>
          {t("tracking_page:ThemeImageChange")}
        </Button>
        <Button fullWidth variant="tertiary" tone="critical" onClick={() => void handleRemove()} disabled={busy}>
          {t("common:Remove")}
        </Button>
      </div>
    </div>
  ) : null

  return (
    <div className="w-full">
      <SpinnerContainer loading={busy}>
        {banner?.url ? previewState : emptyState}
      </SpinnerContainer>

      <ThemeImageSelectModal
        open={modalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmSelection}
        currentImageId={banner?.shopifyFileId}
      />
    </div>
  )
}

export default ImageUpload
